import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import API_URL from "../../api/API";
import { Lock, Pencil } from "lucide-react";
import { RichTextEditor } from "../../components/RichTextEditor";
import { replacePlaceholders, fixProseSpacing, normalizeHtmlEntities } from "../../api/helpers";

const BusinessSmartTemplateStep6 = () => {

  const { waiver } = useOutletContext();  
  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();
  const { waiverId } = useParams();


  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editorKey, setEditorKey] = useState(0);  //Rich Text Editor

  const [isDirty, setIsDirty] = useState(false); //Auto Save - to track changes
  const [saving, setSaving] = useState(false); //Prevent overlapping saves (VERY IMPORTANT)

  const [waiverData, setWaiver] = useState(null);  
  const [governingData, setGoverningData] = useState([]);


  useEffect(() => {

    if (!business) {
      navigate("/business/login");
      return;
    }

    const fetchWaiver = async () => {

      try {

        const res = await fetch(
          `${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}`,
          {
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname
            }
          }
        );

        const data = await res.json();

        if (data.success) {

          const ordered = (data.templates.adultFull.sections || []).sort(
            (a, b) => a.order - b.order
          );

          setWaiver(data.waiver);   // ⭐ VERY IMPORTANT

          setSections(ordered);

          /* Governing Law must be second last, not last section */
          const governingSection = {
            _id: "governing-laws",
            title: "Governing Laws",
            status: "locked",
            system: true,
          };

          const lastSection = ordered[ordered.length - 1];

          setSections([
            ...ordered.slice(0, -1), // all except last
            governingSection,        // second last
            lastSection              // keep original last as last
          ]);

        }

      } catch (err) {
        console.error("Failed loading waiver sections", err);
      } finally {
        setLoading(false);
      }

    };

    fetchWaiver();

  }, [business, waiverId, navigate]);


  useEffect(() => {
    if (!waiver?.locations?.length) return;

    const loadGoverningLaws = async () => {
      try {
        // 1️⃣ get ALL business locations
        const locRes = await fetch(API_URL.BUSINESS_LOCATIONS, {
          headers: {
            Authorization: `Bearer ${business.token}`,
          },
        });

        const locJson = await locRes.json();

        // 2️⃣ keep only waiver locations
        /*const locations = locJson.locations.filter(loc =>
          waiver.locations.includes(loc._id)
        );*/

        const waiverLocationIds = waiver.locations.map(l =>
          String(typeof l === "string" ? l : l._id)
        );

        const locations = locJson.locations.filter(loc =>
          waiverLocationIds.includes(String(loc._id))
        );

        // 3️⃣ unique states
        const states = [...new Set(locations.map(l => l.state))];

        // 4️⃣ get clauses
        const clauseRes = await fetch(
          `${API_URL.GET_STATE_CLAUSES_FOR_WAIVER}/${waiverId}?states=${states.join(",")}`,
          {
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname
            },
          }
        );

        const clauseJson = await clauseRes.json();

        // 5️⃣ build UI data per location
        const uiData = locations.map(loc => ({
          locationName: loc.name,
          state: loc.state,
          clause:
            clauseJson.data.find(
              c =>
                c.state.trim().toLowerCase() ===
                loc.state.trim().toLowerCase()
            )?.body ||
            "<p>No governing law found for this state.</p>",
        }));

        setGoverningData(uiData);
      } catch (err) {
        console.error("Failed loading governing laws", err);
      }
    };

    loadGoverningLaws();
  }, [waiver, waiverId, business.token]);

 
  if (loading) {
    return (
      <div className="p-8">
        <div>Loading...</div>
      </div>
    );
  }

  //Placeholder replacement logic
  const businessName = waiver?.businessId?.businessName || "";
  const businessEmail = waiver?.businessId?.email || "";
  const county =  waiver?.locations?.[0]?.county || "";

  const phValues = {
    BUSINESS_NAME: businessName,
    BUSINESS_EMAIL: businessEmail,
    COUNTY: county,
    MINOR_FULL_NAME: "the minor",
    MINOR_FIRST_NAME: "the minor",
    PREVIOUS_SIGNED_DATE: "THE PREVIOUS SIGNED DATE",
  };
 
  return (
    <> 
    <div className="p-8">
        <h2 className="text-2xl font-semibold mb-6">
            Waiver Content
        </h2>

        <div className="space-y-8">

            {sections.map((section, index) => (

                <div
                key={section._id || index}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
                >

                {/* Section Header */}
                <div className="border-b px-6 py-4">

                    <div className="flex items-center gap-2">

                    <h3 className="font-semibold text-lg">
                        {normalizeHtmlEntities(section.title)}
                    </h3>
                    
                    </div>

                </div>

                {/* Section Content */}
                <div className="p-6">

                    {section.system ? (

                    <div className="space-y-8">

                        {governingData.map((item, idx) => (

                        <div
                            key={idx}
                            className="border rounded-lg p-6"
                        >

                            <h4 className="text-lg font-semibold mb-3">
                            {item.locationName} ({item.state})
                            </h4>

                            <div
                            className="prose max-w-none"
                            dangerouslySetInnerHTML={{
                                __html: fixProseSpacing(
                                normalizeHtmlEntities(
                                    replacePlaceholders(
                                    item.clause,
                                    phValues,
                                    true,
                                    false
                                    )
                                )
                                ),
                            }}
                            />

                        </div>

                        ))}

                    </div>

                    ) : (

                    <div
                        className="
                        prose
                        max-w-none
                        [&_ul]:list-disc
                        [&_ul]:ml-6
                        [&_ol]:list-decimal
                        [&_ol]:ml-6
                        [&_li]:mb-2
                        [&_p]:mb-4
                        "
                        dangerouslySetInnerHTML={{
                        __html: fixProseSpacing(
                            normalizeHtmlEntities(
                            replacePlaceholders(
                                section.content,
                                phValues,
                                true,
                                false
                            )
                            )
                        ),
                        }}
                    />

                    )}

                </div>

                </div>

            ))}

        </div>

        {/* }
        <div className="flex gap-6">

            {/* LEFT SECTION NAV * /}

            <div className="w-72 bg-white rounded-xl shadow-sm p-4">

              <div className="font-semibold mb-4">
                Waiver Sections
              </div>

              {sections.map((section, index) => {

                const locked = section.status === "locked";

                return (

                  <div
                    key={section._id || index}
                    onClick={() => handleSectionChange(index)}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg mb-2 cursor-pointer
                    ${
                      activeIndex === index
                        ? "bg-black text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >

                    <span className="text-sm">
                      {normalizeHtmlEntities(section.title)}
                    </span>

                    {locked ? (
                      <Lock size={14} />
                    ) : (
                      <Pencil size={14} />
                    )}

                  </div>

                );

              })}

            </div>



            {/* RIGHT SECTION * /}

            <div className="flex-1 bg-white rounded-xl shadow-sm">

              { activeSection && (

                <>

                  <div className="border-b px-6 py-4">

                    <div className="font-semibold">
                      {normalizeHtmlEntities(activeSection.title)}
                    </div>

                    <div className="text-sm text-muted-foreground">
                      {activeSection.status === "locked"
                        ? "This section is locked and cannot be edited."
                        : "Edit the content of this section."}
                    </div>

                  </div>

                  <div className="p-6">

                    {activeSection.system ? (

                      <div className="space-y-8">
                        {governingData.map((item, idx) => (
                          <div key={idx} className="border p-6 rounded-lg">
                            <h3 className="text-xl font-semibold mb-3">
                              {item.locationName} ({item.state})
                            </h3>
                            <div
                              className="prose max-w-none"
                                dangerouslySetInnerHTML={{
                                __html: fixProseSpacing(
                                          normalizeHtmlEntities(
                                            replacePlaceholders(item.clause, phValues, true, false)
                                          )
                                        ),
                              }}
                            />
                          </div>
                        ))}
                      </div>

                    ) : activeSection.status === "locked" ? (

                      <div
                        className="prose max-w-none [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-2 [&_p]:mb-4"
                        dangerouslySetInnerHTML={{
                          __html: fixProseSpacing(
                                    normalizeHtmlEntities
                                    (
                                      replacePlaceholders(activeSection.content, phValues, true, false)
                                    )
                                  )
                          }}
                      />

                    ) : (

                      <div
                        className="prose max-w-none [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-2 [&_p]:mb-4"
                        dangerouslySetInnerHTML={{
                          __html: fixProseSpacing(
                                    normalizeHtmlEntities
                                    (
                                      replacePlaceholders(activeSection.content, phValues, true, false)
                                    )
                                  )
                          }}
                      />

                    )}

                  </div>
                  

                </>

              )}

            </div>

        </div>
        */}



    </div>

    </>
  );

};

export default BusinessSmartTemplateStep6;